import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Archive, FileText, Globe, ImageIcon, Inbox, MousePointerClick, Pencil, Sparkles, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { cn, formatRelativeTime } from "@/lib/utils";
import {
    archiveAdvertiser,
    deleteAdvertiser,
    getAdvertiser,
    getAdvertiserAssignmentLog,
    getAdvertiserDashboard,
    updateAdvertiser,
    type AdvertiserDashboardDto,
    type AdvertiserDto,
    type UpdateAdvertiserRequest,
} from "@/api/advertisers";
import { DealsPanel } from "@/components/deals-panel";
import { PostbackTokensCard } from "@/components/postback-tokens-card";
import { listDomainsPaged } from "@/api/domains";
import { listPagesPaged } from "@/api/pages";
import { searchLeads } from "@/api/leads";
import { searchVisits } from "@/api/visits";
import type { DomainDto, LeadDto, PageDto, VisitDto } from "@/types/api";
import type { DraftDto } from "@/types/constructor";

type TabKey = "overview" | "deals" | "domains" | "pages" | "drafts" | "banners" | "leads" | "sessions" | "audit";

const TABS: { key: TabKey; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "deals", label: "Deals" },
    { key: "domains", label: "Domains" },
    { key: "pages", label: "Pages" },
    { key: "drafts", label: "Drafts" },
    { key: "banners", label: "Banners" },
    { key: "leads", label: "Leads" },
    { key: "sessions", label: "Sessions" },
    { key: "audit", label: "Audit log" },
];

const STATUS_VARIANT: Record<string, "success" | "muted" | "warning" | "default"> = {
    active: "success",
    paused: "warning",
    archived: "muted",
};

export function AdvertiserDetailPage(): React.ReactNode {
    const { id } = useParams<{ id: string }>();
    const advertiserId = Number(id);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const [tab, setTab] = React.useState<TabKey>("overview");
    const [editOpen, setEditOpen] = React.useState(false);

    const advertiserQuery = useQuery({
        queryKey: ["advertiser", advertiserId],
        queryFn: () => getAdvertiser(advertiserId),
        enabled: !Number.isNaN(advertiserId),
    });
    const dashboardQuery = useQuery({
        queryKey: ["advertiser", advertiserId, "dashboard"],
        queryFn: () => getAdvertiserDashboard(advertiserId),
        enabled: !Number.isNaN(advertiserId),
    });

    const archiveMut = useMutation({
        mutationFn: () => archiveAdvertiser(advertiserId),
        onSuccess: () => {
            toast.success("Advertiser archived");
            queryClient.invalidateQueries({ queryKey: ["advertiser", advertiserId] });
            queryClient.invalidateQueries({ queryKey: ["advertisers", "list"] });
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Archive failed")),
    });
    const deleteMut = useMutation({
        mutationFn: () => deleteAdvertiser(advertiserId),
        onSuccess: () => {
            toast.success("Advertiser deleted");
            queryClient.invalidateQueries({ queryKey: ["advertisers", "list"] });
            navigate("/advertisers");
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Delete failed — clear FK references first")),
    });

    if (advertiserQuery.isLoading) {
        return <div className="flex min-h-[50vh] items-center justify-center"><Spinner size="lg" /></div>;
    }
    if (!advertiserQuery.data) {
        return (
            <div className="px-6 py-12">
                <EmptyState
                    title="Advertiser not found"
                    description="It may have been deleted. Head back to the list."
                    action={<Button onClick={() => navigate("/advertisers")}>Back to advertisers</Button>}
                />
            </div>
        );
    }
    const adv = advertiserQuery.data;
    const dashboard = dashboardQuery.data;
    const variant = STATUS_VARIANT[adv.status] ?? "default";

    return (
        <>
            <PageHeader
                title={adv.name}
                breadcrumbs={[
                    { label: "Advertisers", href: "/advertisers" },
                    { label: adv.name },
                ]}
                description={
                    <span className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="font-mono">{adv.slug}</Badge>
                        <Badge variant={variant}>{adv.status}</Badge>
                        {adv.contactEmail && <span className="text-muted-foreground">· {adv.contactEmail}</span>}
                        {adv.billingCurrency && <span className="text-muted-foreground">· {adv.billingCurrency}</span>}
                        <span className="text-muted-foreground">· updated {formatRelativeTime(adv.updatedAt)}</span>
                    </span>
                }
                actions={
                    <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditOpen(true)} data-testid="advertiser-edit-button">
                            <Pencil className="h-3.5 w-3.5" /> Edit
                        </Button>
                        {adv.status !== "archived" && (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => confirm.show({
                                    title: `Archive "${adv.name}"?`,
                                    description: "Hides the advertiser from active pickers; existing attribution stays untouched.",
                                    confirmLabel: "Archive",
                                    onConfirm: async () => { await archiveMut.mutateAsync(); },
                                })}
                                data-testid="advertiser-archive-button"
                            >
                                <Archive className="h-3.5 w-3.5" /> Archive
                            </Button>
                        )}
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => confirm.show({
                                title: `Delete "${adv.name}"?`,
                                description: "Permanent. Blocked if any pages, drafts, domains or banners still reference this advertiser — reassign or clear those first.",
                                destructive: true,
                                confirmLabel: "Delete advertiser",
                                onConfirm: async () => { await deleteMut.mutateAsync(); },
                            })}
                            data-testid="advertiser-delete-button"
                        >
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                        </Button>
                    </div>
                }
            />

            <div className="space-y-6 px-6 py-4">
                <KpiCards dashboard={dashboard} />

                {/* Tabs */}
                <div className="border-b border-border">
                    <nav className="flex flex-wrap gap-1" aria-label="Advertiser sections">
                        {TABS.map((t) => (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setTab(t.key)}
                                className={cn(
                                    "border-b-2 px-3 py-2 text-sm font-medium transition-colors",
                                    tab === t.key
                                        ? "border-primary text-foreground"
                                        : "border-transparent text-muted-foreground hover:text-foreground"
                                )}
                                data-testid={`advertiser-tab-${t.key}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {tab === "overview" && <OverviewTab adv={adv} dashboard={dashboard} />}
                {tab === "deals" && (
                    <div className="space-y-6">
                        <DealsPanel side="advertiser" counterpartyId={advertiserId} />
                        <PostbackTokensCard advertiserId={advertiserId} />
                    </div>
                )}
                {tab === "domains" && <DomainsTab advertiserId={advertiserId} />}
                {tab === "pages" && <PagesTab advertiserId={advertiserId} />}
                {tab === "drafts" && <DraftsTab advertiserId={advertiserId} />}
                {tab === "banners" && <BannersTab dashboard={dashboard} />}
                {tab === "leads" && <LeadsTab advertiserId={advertiserId} />}
                {tab === "sessions" && <SessionsTab advertiserId={advertiserId} />}
                {tab === "audit" && <AuditTab advertiserId={advertiserId} />}
            </div>

            <EditAdvertiserDialog
                advertiser={adv}
                open={editOpen}
                onOpenChange={setEditOpen}
                onSaved={() => {
                    queryClient.invalidateQueries({ queryKey: ["advertiser", advertiserId] });
                    queryClient.invalidateQueries({ queryKey: ["advertisers", "list"] });
                }}
            />
            {confirm.dialog}
        </>
    );
}

function KpiCards({ dashboard }: { dashboard: AdvertiserDashboardDto | undefined }): React.ReactNode {
    const cells: { label: string; value: React.ReactNode }[] = dashboard ? [
        { label: "Domains", value: dashboard.domainsOwned },
        { label: "Pages", value: dashboard.pagesOwned },
        { label: "Drafts", value: dashboard.draftsOwned },
        { label: "Banners", value: dashboard.bannersOwned },
        { label: "LLM spend", value: microsToUsd(dashboard.llmCostUsdMicros) },
        { label: "Domain spend", value: microsToUsd(dashboard.domainSpendUsdMicros) },
        { label: "Image-gen spend", value: microsToUsd(dashboard.imageGenCostUsdMicros) },
        { label: "Total spend", value: microsToUsd(dashboard.totalSpendUsdMicros), accent: true } as never,
    ] : Array.from({ length: 8 }).map(() => ({ label: "—", value: <span className="text-muted-foreground">—</span> }));
    return (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
            {cells.map((c, i) => (
                <div
                    key={i}
                    className={cn(
                        "rounded-md border border-border bg-card p-3",
                        (c as { accent?: boolean }).accent && "border-primary/30 bg-primary/5",
                    )}
                    data-testid={`advertiser-kpi-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.label}</div>
                    <div className="mt-1 text-xl font-semibold tabular-nums">{c.value}</div>
                </div>
            ))}
        </div>
    );
}

function microsToUsd(value: number | null | undefined): string {
    if (value == null) return "—";
    if (value === 0) return "$0.00";
    return `$${(value / 1_000_000).toFixed(2)}`;
}

function OverviewTab({ adv, dashboard }: { adv: AdvertiserDto; dashboard: AdvertiserDashboardDto | undefined }): React.ReactNode {
    return (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-md border border-border bg-card p-4">
                <div className="mb-3 text-sm font-medium">Advertiser details</div>
                <dl className="grid grid-cols-3 gap-y-2 text-sm">
                    <dt className="text-muted-foreground">Slug</dt>
                    <dd className="col-span-2 font-mono">{adv.slug}</dd>
                    <dt className="text-muted-foreground">Contact email</dt>
                    <dd className="col-span-2">{adv.contactEmail ?? "—"}</dd>
                    <dt className="text-muted-foreground">Billing currency</dt>
                    <dd className="col-span-2">{adv.billingCurrency ?? "—"}</dd>
                    <dt className="text-muted-foreground">Status</dt>
                    <dd className="col-span-2">{adv.status}</dd>
                    <dt className="text-muted-foreground">Created</dt>
                    <dd className="col-span-2">{formatRelativeTime(adv.createdAt)}</dd>
                    {adv.archivedAt && (
                        <>
                            <dt className="text-muted-foreground">Archived</dt>
                            <dd className="col-span-2">{formatRelativeTime(adv.archivedAt)}</dd>
                        </>
                    )}
                </dl>
                {adv.notes && (
                    <div className="mt-3 border-t pt-3 text-sm text-muted-foreground whitespace-pre-wrap">{adv.notes}</div>
                )}
            </div>
            <div className="rounded-md border border-border bg-card p-4">
                <div className="mb-3 text-sm font-medium">Cost breakdown</div>
                {dashboard ? (
                    <ul className="space-y-2 text-sm">
                        <CostRow label="LLM" value={dashboard.llmCostUsdMicros} total={dashboard.totalSpendUsdMicros} />
                        <CostRow label="Domain registration" value={dashboard.domainSpendUsdMicros} total={dashboard.totalSpendUsdMicros} />
                        <CostRow label="Image generation" value={dashboard.imageGenCostUsdMicros} total={dashboard.totalSpendUsdMicros} />
                        <li className="mt-2 flex items-center justify-between border-t pt-2 font-semibold">
                            <span>Total</span>
                            <span className="tabular-nums">{microsToUsd(dashboard.totalSpendUsdMicros)}</span>
                        </li>
                    </ul>
                ) : <Spinner size="sm" />}
            </div>
        </div>
    );
}

function CostRow({ label, value, total }: { label: string; value: number; total: number }): React.ReactNode {
    const pct = total > 0 ? (value / total) * 100 : 0;
    return (
        <li>
            <div className="flex items-center justify-between text-sm">
                <span>{label}</span>
                <span className="tabular-nums">{microsToUsd(value)}</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded bg-muted">
                <div className="h-full bg-primary" style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
        </li>
    );
}

function DomainsTab({ advertiserId }: { advertiserId: number }): React.ReactNode {
    const q = useQuery({
        queryKey: ["domains", "by-advertiser", advertiserId],
        queryFn: () => listDomainsPaged({ size: 200, sort: "createdAt,desc", advertiserId }),
    });
    const rows: DomainDto[] = q.data?.content ?? [];
    if (q.isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;
    if (rows.length === 0) return <EmptyState icon={<Globe className="h-6 w-6" />} title="No domains attributed" />;
    return (
        <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="px-3 py-2 text-left">Hostname</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Serving page</th>
                        <th className="px-3 py-2 text-left">Purchased</th>
                        <th className="px-3 py-2 text-left">Updated</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map((d) => (
                        <tr key={d.id} className="hover:bg-muted/30">
                            <td className="px-3 py-2"><Link to={`/domains/${d.id}`} className="font-medium hover:underline">{d.hostname}</Link></td>
                            <td className="px-3 py-2"><Badge variant="outline">{d.status}</Badge></td>
                            <td className="px-3 py-2 text-muted-foreground">
                                {d.activePageId ? <Link to={`/pages/${d.activePageId}`} className="hover:underline">{d.activePageName ?? `#${d.activePageId}`}</Link> : "—"}
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">{formatRelativeTime(d.purchasedAt)}</td>
                            <td className="px-3 py-2 text-muted-foreground">{formatRelativeTime(d.updatedAt)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function PagesTab({ advertiserId }: { advertiserId: number }): React.ReactNode {
    const q = useQuery({
        queryKey: ["pages", "by-advertiser", advertiserId],
        queryFn: () => listPagesPaged({ size: 200, sort: "updatedAt,desc", advertiserId }),
    });
    const rows: PageDto[] = q.data?.content ?? [];
    if (q.isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;
    if (rows.length === 0) return <EmptyState icon={<FileText className="h-6 w-6" />} title="No pages attributed" />;
    return (
        <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-left">Updated</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map((p) => (
                        <tr key={p.id} className="hover:bg-muted/30">
                            <td className="px-3 py-2"><Link to={`/pages/${p.id}`} className="font-medium hover:underline">{p.name}</Link></td>
                            <td className="px-3 py-2 text-muted-foreground">{p.description ?? "—"}</td>
                            <td className="px-3 py-2 text-muted-foreground">{formatRelativeTime(p.updatedAt)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function DraftsTab({ advertiserId }: { advertiserId: number }): React.ReactNode {
    // listDrafts() doesn't expose advertiserId yet; hit the controller directly.
    // Backend caps at size:200 so this is bounded.
    const q = useQuery({
        queryKey: ["drafts", "by-advertiser", advertiserId],
        queryFn: async () => {
            const { data } = await api.get<{ content: DraftDto[] }>("/admin/constructor/drafts", {
                params: { size: 200, advertiserId },
            });
            return data.content ?? [];
        },
    });
    const rows = q.data ?? [];
    if (q.isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;
    if (rows.length === 0) return <EmptyState icon={<Sparkles className="h-6 w-6" />} title="No drafts attributed" />;
    return (
        <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Owner</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Updated</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map((d) => (
                        <tr key={d.id} className="hover:bg-muted/30">
                            <td className="px-3 py-2">
                                <Link to={`/constructor/draft/${d.id}`} className="font-medium hover:underline">{d.name}</Link>
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">{d.ownerEmail ?? "—"}</td>
                            <td className="px-3 py-2"><Badge variant="outline">{d.status}</Badge></td>
                            <td className="px-3 py-2 text-muted-foreground">{formatRelativeTime(d.updatedAt)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function BannersTab({ dashboard }: { dashboard: AdvertiserDashboardDto | undefined }): React.ReactNode {
    return (
        <EmptyState
            icon={<ImageIcon className="h-6 w-6" />}
            title={`${dashboard?.bannersOwned ?? 0} banner${dashboard?.bannersOwned === 1 ? "" : "s"} attributed`}
            description="Open the page that owns each banner to inspect or download the assets — banners hang off their parent page."
        />
    );
}

function LeadsTab({ advertiserId }: { advertiserId: number }): React.ReactNode {
    const q = useQuery({
        queryKey: ["leads", "by-advertiser", advertiserId],
        queryFn: () => searchLeads({ size: 100, advertiserId }),
    });
    const rows: LeadDto[] = q.data?.content ?? [];
    if (q.isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;
    if (rows.length === 0) return <EmptyState icon={<Inbox className="h-6 w-6" />} title="No leads attributed yet" />;
    return (
        <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="px-3 py-2 text-left">When</th>
                        <th className="px-3 py-2 text-left">Domain</th>
                        <th className="px-3 py-2 text-left">Page</th>
                        <th className="px-3 py-2 text-left">Source</th>
                        <th className="px-3 py-2 text-left">Country</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map((l) => (
                        <tr key={l.id} className="hover:bg-muted/30">
                            <td className="px-3 py-2">
                                <Link to={`/leads?id=${l.id}`} className="hover:underline">{formatRelativeTime(l.createdAt)}</Link>
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">{l.domainHostname ?? `#${l.domainId}`}</td>
                            <td className="px-3 py-2 text-muted-foreground">{l.pageName ?? "—"}</td>
                            <td className="px-3 py-2 text-muted-foreground">{l.primarySource ?? "—"}</td>
                            <td className="px-3 py-2 text-muted-foreground">{l.country ?? "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function SessionsTab({ advertiserId }: { advertiserId: number }): React.ReactNode {
    const q = useQuery({
        queryKey: ["visits", "by-advertiser", advertiserId],
        queryFn: () => searchVisits({ size: 100, advertiserId }),
    });
    const rows: VisitDto[] = q.data?.content ?? [];
    if (q.isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;
    if (rows.length === 0) return <EmptyState icon={<MousePointerClick className="h-6 w-6" />} title="No sessions attributed yet" />;
    return (
        <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="px-3 py-2 text-left">When</th>
                        <th className="px-3 py-2 text-left">Domain</th>
                        <th className="px-3 py-2 text-left">Page</th>
                        <th className="px-3 py-2 text-left">Source</th>
                        <th className="px-3 py-2 text-left">Country</th>
                        <th className="px-3 py-2 text-left">Converted</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map((v) => (
                        <tr key={v.id} className="hover:bg-muted/30">
                            <td className="px-3 py-2 text-muted-foreground">{formatRelativeTime(v.createdAt)}</td>
                            <td className="px-3 py-2 text-muted-foreground">{v.domainHostname ?? `#${v.domainId}`}</td>
                            <td className="px-3 py-2 text-muted-foreground">{v.pageName ?? "—"}</td>
                            <td className="px-3 py-2 text-muted-foreground">{v.primarySource ?? "—"}</td>
                            <td className="px-3 py-2 text-muted-foreground">{v.country ?? "—"}</td>
                            <td className="px-3 py-2">
                                {v.convertedLeadId ? (
                                    <Link to={`/leads?id=${v.convertedLeadId}`} className="text-emerald-600 hover:underline">→ lead #{v.convertedLeadId}</Link>
                                ) : <span className="text-muted-foreground">—</span>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function AuditTab({ advertiserId }: { advertiserId: number }): React.ReactNode {
    const q = useQuery({
        queryKey: ["advertiser", advertiserId, "assignment-log"],
        queryFn: () => getAdvertiserAssignmentLog(advertiserId, 200),
    });
    const rows = q.data ?? [];
    if (q.isLoading) return <div className="flex justify-center py-8"><Spinner /></div>;
    if (rows.length === 0) {
        return <EmptyState title="No assignment history yet" description="Rows show up here every time an admin reassigns a page, draft, domain or banner." />;
    }
    return (
        <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="px-3 py-2 text-left">When</th>
                        <th className="px-3 py-2 text-left">Entity</th>
                        <th className="px-3 py-2 text-left">Change</th>
                        <th className="px-3 py-2 text-left">Admin</th>
                        <th className="px-3 py-2 text-left">Reason</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map((row) => {
                        const link = entityLink(row.entityType, row.entityId);
                        const direction = directionForLog(row.oldOwnerId, row.newOwnerId, advertiserId);
                        return (
                            <tr key={row.id} className="hover:bg-muted/30">
                                <td className="px-3 py-2 text-muted-foreground">{formatRelativeTime(row.changedAt)}</td>
                                <td className="px-3 py-2">
                                    <Badge variant="outline" className="font-mono text-[10px] uppercase">{row.entityType}</Badge>
                                    {link ? (
                                        <Link to={link} className="ml-2 text-sm hover:underline">#{row.entityId}</Link>
                                    ) : (
                                        <span className="ml-2 text-sm text-muted-foreground">#{row.entityId}</span>
                                    )}
                                </td>
                                <td className="px-3 py-2">
                                    <Badge variant={direction === "assigned" ? "success" : direction === "removed" ? "warning" : "muted"}>
                                        {direction}
                                    </Badge>
                                </td>
                                <td className="px-3 py-2 text-muted-foreground">{row.changedByEmail ?? `admin #${row.changedByAdminId}`}</td>
                                <td className="px-3 py-2 text-muted-foreground">{row.reason ?? "—"}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function directionForLog(oldOwnerId: number | null, newOwnerId: number | null, focus: number): "assigned" | "removed" | "transferred" {
    if (newOwnerId === focus && oldOwnerId !== focus) return "assigned";
    if (oldOwnerId === focus && newOwnerId !== focus) return "removed";
    return "transferred";
}

function entityLink(entityType: string, entityId: number): string | null {
    switch (entityType) {
        case "page": return `/pages/${entityId}`;
        case "page_draft": return `/constructor/draft/${entityId}`;
        case "domain": return `/domains/${entityId}`;
        case "banner": return null;
        case "campaign": return `/campaigns/${entityId}`;
        case "ad_source": return "/ad-sources";
        default: return null;
    }
}

function EditAdvertiserDialog({
    advertiser,
    open,
    onOpenChange,
    onSaved,
}: {
    advertiser: AdvertiserDto;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSaved: () => void;
}): React.ReactNode {
    const [name, setName] = React.useState(advertiser.name);
    const [contactEmail, setContactEmail] = React.useState(advertiser.contactEmail ?? "");
    const [billingCurrency, setBillingCurrency] = React.useState(advertiser.billingCurrency ?? "USD");
    const [notes, setNotes] = React.useState(advertiser.notes ?? "");
    const [status, setStatus] = React.useState(advertiser.status);

    React.useEffect(() => {
        if (open) {
            setName(advertiser.name);
            setContactEmail(advertiser.contactEmail ?? "");
            setBillingCurrency(advertiser.billingCurrency ?? "USD");
            setNotes(advertiser.notes ?? "");
            setStatus(advertiser.status);
        }
    }, [open, advertiser]);

    const saveMut = useMutation({
        mutationFn: (req: UpdateAdvertiserRequest) => updateAdvertiser(advertiser.id, req),
        onSuccess: () => {
            toast.success("Saved");
            onOpenChange(false);
            onSaved();
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Save failed")),
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="advertiser-edit-dialog">
                <DialogHeader>
                    <DialogTitle>Edit advertiser</DialogTitle>
                    <DialogDescription>Slug is immutable — create a new advertiser if you need a different one.</DialogDescription>
                </DialogHeader>
                <form
                    className="space-y-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        saveMut.mutate({
                            name: name.trim() || null,
                            contactEmail: contactEmail.trim() || null,
                            billingCurrency: billingCurrency.trim() || null,
                            notes: notes.trim() || null,
                            status,
                        });
                    }}
                >
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="adv-edit-name">Name</Label>
                            <Input id="adv-edit-name" value={name} onChange={(e) => setName(e.target.value)} required />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="adv-edit-status">Status</Label>
                            <select
                                id="adv-edit-status"
                                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <option value="active">active</option>
                                <option value="paused">paused</option>
                                <option value="archived">archived</option>
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="adv-edit-email">Contact email</Label>
                            <Input id="adv-edit-email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="adv-edit-currency">Billing currency</Label>
                            <Input id="adv-edit-currency" value={billingCurrency} onChange={(e) => setBillingCurrency(e.target.value.toUpperCase().slice(0, 3))} />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="adv-edit-notes">Notes</Label>
                        <Textarea id="adv-edit-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saveMut.isPending}>Cancel</Button>
                        <Button type="submit" disabled={saveMut.isPending} data-testid="advertiser-edit-submit">
                            {saveMut.isPending ? <Spinner size="sm" /> : "Save changes"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
